import React, { useState, useRef, useEffect } from 'react';
import {
    Modal,
    Button,
    Step,
    Icon,
    StepTitle,
    StepDescription,
    StepContent,
    Container,
} from 'semantic-ui-react';
import { calcTotal, PAYMENT_METHOD } from '../../utils/business';
import { db } from '../../utils/firebase';
import { ref, onValue, off } from "firebase/database";
import { useAuth } from "../../App";
import { CartStep } from './order/CartStep';
import { ShippingStep } from './order/ShippingStep';
import { BillingStep } from './order/BillingStep';
import { ReviewStep } from './order/ReviewStep';
import { ConfirmStep } from './order/ConfirmStep';
import { useIsMobile } from '../../utils/hooks';

export default function OrderDialog({ open, onClose, onComplete, orderItem }) {
    const isMobile = useIsMobile();
    const { user } = useAuth();

    const shippingRef = useRef(null);
    const billingRef = useRef(null);
    const reviewRef = useRef(null);

    const [cart] = useState({
        items: [
            orderItem
        ],
    });

    const [shipping, setShipping] = useState({});
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [billing, setBilling] = useState({});
    const [consents, setConsents] = useState({});
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [orderId, setOrderId] = useState(null);

    const steps = [
        { key: 'cart', icon: 'cart', title: 'Košík', description: 'Prehľad produktov' },
        { key: 'shipping', icon: 'truck', title: 'Doručenie', description: 'Adresa' },
        { key: 'billing', icon: 'credit card', title: 'Fakturácia', description: 'Údaje & platba' },
        { key: 'review', icon: 'info circle', title: 'Kontrola', description: 'Súhlas & potvrdenie' },
        { key: 'confirm', icon: 'check', title: 'Hotovo', description: 'Potvrdenie objednávky' },
    ];

    async function handleNext() {
        if (step === 1 && shippingRef.current) {
            const ok = shippingRef.current.validate();
            // if (!ok) return; TODO: activate
            setShipping(shippingRef.current.getValues());
        }
        if (step === 2 && billingRef.current) {
            const ok = billingRef.current.validate();
            // if (!ok) return; TODO: activate
            setBilling(billingRef.current.getValues());
        }
        setStep((s) => s + 1);
    }

    function handleBack() {
        if (step > 0) setStep((s) => s - 1);
    }

    async function handleSubmitOrder() {
        if (reviewRef.current) {
            const ok = reviewRef.current.validate();
            if (!ok) return;
            const actualConsents = reviewRef.current.getValues()
            setConsents(actualConsents);
            setLoading(true);
            if (billing.paymentMethod == PAYMENT_METHOD.CASH_ON_DELIVERY) {
                await new Promise((r) => setTimeout(r, 800));
                setLoading(false);
                setStep((s) => s + 1);
                if (typeof onComplete === 'function') {
                    onComplete({ shipping, billing, cart, consents });
                }
            }
            if (billing.paymentMethod == PAYMENT_METHOD.GOPAY) {
                if (typeof onComplete === 'function') {
                    const newOrderId = await onComplete({ shipping, billing, cart, consents });

                    setOrderId(newOrderId); //for tracking status in next step

                    const paymentRef = ref(
                        db, `/users/${user.uid}/orders/${newOrderId}/status/payment`
                    );
                    const unsubscribe = onValue(paymentRef, (snapshot) => {
                        const paymentStatus = snapshot.val();
                        if (!paymentStatus) {
                            return; //no value yet, still waiting for gopay payment initialization
                        }
                        console.log("New payment status:", paymentStatus);
                        off(paymentRef, "value");
                        setLoading(false);
                        setPaymentStatus(paymentStatus);
                        setStep((s) => s + 1);
                    });
                } else {
                    //TODO: fallback if onComplete not function
                }
            }
        }
    }

    function StepCardContent() {
        const totals = calcTotal([orderItem]);
        switch (step) {
            case 0: return <CartStep cart={cart} totals={totals} />;
            case 1: return <ShippingStep ref={shippingRef} initialData={shipping} />;
            case 2: return <BillingStep ref={billingRef} initialData={billing} shippingData={shipping} />;
            case 3: return <ReviewStep ref={reviewRef} initialData={consents} cart={cart} totals={totals} shippingData={shipping} billingData={billing} />;
            case 4: return <ConfirmStep
                paymentStatus={paymentStatus}
                setPaymentStatus={setPaymentStatus}
                billing={billing}
                cart={cart}
                orderId={orderId}
                orderItem={orderItem}
                setLoading={setLoading}
                db={db}
                user={user}
            />;
            default: return null;
        }
    }

    return (
        <Modal open={open} onClose={onClose} size='large' closeIcon>
            <Modal.Header>Objednávka</Modal.Header>
            <Modal.Content scrolling>
                <Container centered style={{ textAlign: 'center' }}>
                    <Step.Group unstackable size='mini'>
                        {steps.map((s, i) => (
                            <Step
                                key={s.key}
                                active={i === step}
                                completed={i < step}
                                disabled={i > step}
                                style={isMobile ? { padding: 15 } : {}}
                            >
                                {isMobile ? <>
                                    <StepContent>
                                        {i === step ? <StepTitle>{s.title}</StepTitle> : <Icon name={s.icon} />}
                                    </StepContent>
                                </> : <><Icon name={s.icon} />
                                    <StepContent>
                                        <StepTitle>{s.title}</StepTitle>
                                        <StepDescription>{s.description}</StepDescription>
                                    </StepContent>
                                </>}
                            </Step>
                        ))}

                    </Step.Group>
                </Container>

                <div style={{ marginTop: 20 }}>
                    <StepCardContent />
                </div>
            </Modal.Content>
            <Modal.Actions>
                {step > 0 && step < 4 && (
                    <Button color='yellow' onClick={handleBack}>
                        Naspäť
                    </Button>
                )}
                {step < 3 && (
                    <Button color='yellow' onClick={handleNext}>
                        Ďalej
                    </Button>
                )}
                {step === 3 && (
                    <Button color='yellow' loading={loading} onClick={handleSubmitOrder}>
                        Potvrdiť objednávku
                    </Button>
                )}
                {step === 4 && (
                    <Button color='yellow' onClick={onClose}>
                        Zavrieť
                    </Button>
                )}
            </Modal.Actions>
        </Modal>
    );
}
